<template>
  <forms-layout :enable-scroll-event="true">
    <div class="relative flex flex-col min-h-screen">
      <div
        v-if="sharedFormUnavailable"
        data-testid="form-unavailable"
        class="flex flex-col items-center justify-center flex-1 px-8 text-center"
      >
        <p class="text-body-1 font-semibold text-primary">This form isn't available.</p>
        <p class="mt-2 text-body-1 text-grey-60">Check with the leader who sent you the link.</p>
      </div>
      <Loading v-else-if="isLoadingForm" />
      <iframe
        v-else
        ref="formFrame"
        class="w-full h-screen"
        :key="formSrc"
        :src="formSrc"
        :title="$route.params.formName"
        @load="frameHidesBack = false"
      ></iframe>
      <!-- The form's own page can ask for these to go (onFrameMessage). -->
      <div
        v-if="(sharedFormUnavailable || !isLoadingForm) && !frameHidesBack"
        data-testid="form-top-bar"
        class="absolute top-0 left-0 right-0 z-10 flex items-end justify-between px-5 md:px-6 lg:px-8"
      >
        <BackButton class="w-auto" fallback-route="/home" />
        <ion-button
          v-if="canShareForm"
          fill="clear"
          data-testid="form-share-button"
          aria-label="Share this form"
          class="group shrink-0 m-0 w-[44px] h-[44px] min-h-0 [--padding-start:0] [--padding-end:0] [--padding-top:0] [--padding-bottom:0] [--background-activated:transparent] [--background-focused:transparent] [--background-hover:transparent]"
          :disabled="isSharing"
          @click="shareForm"
        >
          <!-- 32px circle in a 44px target, to sit level with BackButton's icon -->
          <span
            class="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-white shadow-sm group-active:bg-grey-50"
          >
            <ShareIcon />
          </span>
        </ion-button>
      </div>
      <Toast />
    </div>
  </forms-layout>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapState } from 'vuex';
import { formHogcOneId } from '@/utils/form-hogc-one-id';
import { IonButton } from '@ionic/vue';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';
import Loading from '@/components/common/LoadingState';
import BackButton from '@/components/common/BackButton';
import ShareIcon from '@/components/common/ShareIcon.vue';
import Toast from '@/components/common/Toast.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { transformForms } from '@/store/modules/cms';
import { canLeaderShareForm, createFormShareLink, fetchSharedForm } from '@/utils/form-share-api';
import { isIosNativeOnly } from '@/utils/platform-check';
import FormsLayout from '@/components/FormsLayout.vue';
import { appendQueryParams } from '@/utils/url-params';

// Re-exported so existing importers (and tests) that reference it from this page
// keep working now that the implementation lives in @/utils/url-params.
export { appendQueryParams };

// Route-query names that may never reach the iframe. The route query is whatever the
// link's author wrote, so it must not be able to supply a value the API issues per
// member through custom prefills. `key` is The Building Board's signed member key: a
// crafted `/forms/building-board?key=…` would otherwise sign the member in as whoever
// the key was minted for — silently so when the custom-prefill fetch fails and there
// is no signed key to take precedence. Other route params keep passing through, since
// existing forms rely on them (campaign, source, …).
export const RESERVED_ROUTE_QUERY_KEYS = ['key'];

// The same rule for names only one form's page reads, kept per form because the names
// are too generic to take from every form. `t` is the AGM web app's single-use launch
// token (agm-rsvp-web reads `?t=`): a crafted `/forms/agm?t=…` would otherwise open the
// AGM page as whoever that token was minted for when the API's own token is missing.
export const RESERVED_ROUTE_QUERY_KEYS_BY_FORM = new Map([['agm', ['t']]]);

const reservedKeysFor = formName => [
  ...RESERVED_ROUTE_QUERY_KEYS,
  ...(RESERVED_ROUTE_QUERY_KEYS_BY_FORM.get(formName) || []),
];

const withoutReservedKeys = (query, formName) => {
  const reserved = reservedKeysFor(formName);
  return Object.fromEntries(
    Object.entries(query || {}).filter(([name]) => !reserved.includes(name)),
  );
};

// Shared with hogc-one-api, which gates both share routes on the same name.
const FORM_SHARE_LINKS_FLAG = 'form-share-links';

export default {
  components: { Loading, BackButton, FormsLayout, ShareIcon, Toast, IonButton },
  data() {
    return {
      customPrefillParams: {},
      customPrefillsLoading: true,
      // Every param from GET /form/prefills, or null to build the URL here
      // (flag off, older API, or the call failed).
      serverPrefills: null,
      // A form this member reached through a leader's share link
      // (/forms/<title>?shared=1) that their own forms list does not hold.
      sharedForm: null,
      // A share link the API refused: shown in place, since home mounts no Toast.
      sharedFormUnavailable: false,
      isSharing: false,
      // The form's page asked for the back button (and share button) to go,
      // on a screen of its own that has its own way back. See onFrameMessage.
      frameHidesBack: false,
    };
  },
  computed: {
    ...mapState('cms', ['forms', 'loading']),
    ...mapState('profile', [
      'dateOfBirth',
      'personalId',
      'id',
      'hasNoMember',
      'appUserId',
      'uin',
      'ministryStatus',
      'stageOfLife',
      'educationLevel',
      'schoolName',
      'isLeader',
      'leadershipRolesFormatted',
      'demography',
    ]),
    ...mapGetters('profile', [
      'email',
      'genderLongAbbr',
      'fullName',
      'localMobileNumber',
      'cgName',
      'pastoralStatus',
    ]),
    isLoadingForm() {
      return this.loading || this.customPrefillsLoading || !this.currentForm;
    },
    currentForm() {
      return this.forms[this.$route.params.formName] || this.sharedForm;
    },
    isSharedLink() {
      return Boolean(this.$route.query.shared);
    },
    // The route query minus the shared marker, which is the app's business, not
    // the form provider's, and minus reserved keys (RESERVED_ROUTE_QUERY_KEYS and
    // this form's RESERVED_ROUTE_QUERY_KEYS_BY_FORM).
    formQuery() {
      // eslint-disable-next-line no-unused-vars
      const { shared, ...query } = this.$route.query;
      return withoutReservedKeys(query, this.$route.params.formName);
    },
    // Only on a form from the leader's OWN list: one they reached through
    // someone else's link is one the API would refuse to let them re-share.
    canShareForm() {
      // Resolved inside the computed, not at module scope, so specs can mock it.
      const { isEnabled } = useFeatureFlags();
      const formTitle = this.$route.params.formName;
      return (
        isEnabled(FORM_SHARE_LINKS_FLAG) &&
        canLeaderShareForm({ isLeader: this.isLeader, formTitle, ownForm: this.forms[formTitle] })
      );
    },
    formSrc() {
      if (!this.currentForm) return '';
      if (this.serverPrefills && this.currentForm.link) {
        return appendQueryParams(this.currentForm.link, this.serverPrefills, this.formQuery);
      }
      const base = this.currentForm.getSrc({
        fullname: this.fullName,
        cg: this.cgName,
        email: this.email,
        uin: this.uin,
        dateOfBirth: this.dateOfBirth,
        localMobileNumber: this.localMobileNumber,
        personalId: this.personalId,
        id: formHogcOneId({
          id: this.id,
          hasNoMember: this.hasNoMember,
          appUserId: this.appUserId,
        }),
        gender: this.genderLongAbbr,
        login: true,
        pastoralStatus: this.pastoralStatus,
        ministryStatus: this.ministryStatus,
        stageOfLife: this.stageOfLife,
        educationLevel: this.educationLevel,
        schoolName: this.schoolName,
        isLeader: this.isLeader,
        leadershipRolesFormatted: this.leadershipRolesFormatted,
        demography: this.demography,
        force: 1,
      });
      return appendQueryParams(base, this.customPrefillParams, this.formQuery);
    },
  },
  watch: {
    forms(formsState) {
      // A share link resolves its own form in ionViewWillEnter, and sends the
      // member home itself if it cannot.
      if (Object.keys(formsState).length > 0 && !this.currentForm && !this.isSharedLink) {
        this.$router.push('/');
      }
    },
  },
  async ionViewWillEnter() {
    this.customPrefillsLoading = true;
    this.serverPrefills = null;
    this.sharedForm = null;
    this.sharedFormUnavailable = false;

    // allSettled, not all: a fast failure must not cut the wait short, because
    // whether the form is in this member's list decides the share-link path
    // below — deciding before the forms request settles could send a member
    // who can open the form normally to the share route, and its refusal.
    const loads = await Promise.allSettled([
      this.$store.dispatch('profile/fetchProfile'),
      this.$store.dispatch('cms/fetchCmsContent', {
        entityName: 'forms',
        stateName: 'forms',
      }),
      this.$store.dispatch('updates/getUpdates', 'all'),
    ]);
    loads
      .filter(load => load.status === 'rejected')
      .forEach(load =>
        console.warn('[Forms] Failed to load form prefill dependencies:', load.reason),
      );

    if (this.isSharedLink && !this.forms[this.$route.params.formName]) {
      this.sharedForm = await this.fetchSharedForm();
      if (!this.sharedForm) {
        this.sharedFormUnavailable = true;
        this.customPrefillsLoading = false;
        return;
      }
    }

    // The API builds every param from the form's CMS checkboxes, so both apps
    // send the same fields. Until the flag is on it answers 404.
    this.serverPrefills = await this.fetchServerPrefills();
    if (this.serverPrefills) {
      this.customPrefillsLoading = false;
      return;
    }

    // Fallback: build the URL here, as before. Ministry status is fetched fresh
    // because MasterLayout — the only other place that fetches it — never
    // mounts when a form opens straight after launch or login.
    try {
      await this.$store.dispatch('profile/fetchMinistryStatus', { force: true });
    } catch (error) {
      console.warn('[Forms] Failed to load ministry status:', error);
    }
    await this.fetchCustomPrefills();
  },
  mounted() {
    window.addEventListener('message', this.onFrameMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onFrameMessage);
  },
  methods: {
    // An embedded page (The Building Board) can ask to hide the floating back
    // button on its own sub-pages, which have their own way back, and to show
    // it again: { type: 'hogc-one:back-button', visible }. Only the form's own
    // frame is heard, and it is told what now shows, so it can drop the gap it
    // leaves under the button. A new page in the frame starts with it shown.
    onFrameMessage(event) {
      const frame = this.$refs.formFrame?.contentWindow;
      if (!frame || event.source !== frame) return;
      const { type, visible } = event.data || {};
      if (type !== 'hogc-one:back-button' || typeof visible !== 'boolean') return;
      this.frameHidesBack = !visible;
      frame.postMessage({ type: 'hogc-one:back-button-state', visible }, event.origin);
    },
    // A leader-shared form, transformed like the forms list, or null. The API
    // opens an opted-in form to every member from N up, and 404s every refusal.
    async fetchSharedForm() {
      const formName = this.$route.params.formName;
      try {
        const raw = await fetchSharedForm(formName, this.$store.state.user.token);
        if (raw?.title !== formName) return null;
        return transformForms([raw])[formName] ?? null;
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.warn('[Forms] Failed to load shared form:', error);
        }
        return null;
      }
    },
    async shareForm() {
      if (this.isSharing) return;
      this.isSharing = true;
      try {
        const url = await this.createShareLink();
        if (!url) {
          this.$store.dispatch('toast/error', "Couldn't create a share link. Please try again.");
          return;
        }

        const { value: canShare } = await Share.canShare().catch(() => ({ value: false }));
        if (canShare) {
          try {
            await Share.share({
              text: "Here's a form for you on the HOGC One App.",
              url,
              dialogTitle: 'Share Form',
            });
          } catch {
            // The leader closed the share sheet.
          }
          return;
        }

        try {
          await Clipboard.write({ string: url });
          this.$store.dispatch('toast/success', 'Link copied.');
        } catch {
          this.$store.dispatch('toast/error', "Couldn't copy the link. Please try again.");
        }
      } finally {
        this.isSharing = false;
      }
    },
    async createShareLink() {
      try {
        return await createFormShareLink(this.$route.params.formName, this.$store.state.user.token);
      } catch (error) {
        console.warn('[Forms] Failed to create share link:', error);
        return null;
      }
    },
    async fetchServerPrefills() {
      if (!this.currentForm?.link) return null;

      try {
        const accessToken = this.$store.state.user.token;
        const encodedFormName = encodeURIComponent(this.$route.params.formName);
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}/form/prefills/${encodedFormName}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        const prefills = data?.data;
        return prefills && typeof prefills === 'object' ? prefills : null;
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.warn('[Forms] Failed to load server prefills:', error);
        }
        return null;
      }
    },
    async fetchCustomPrefills() {
      if (!this.currentForm) {
        this.customPrefillParams = {};
        this.customPrefillsLoading = false;
        return;
      }

      try {
        const accessToken = this.$store.state.user.token;
        const encodedFormName = encodeURIComponent(this.$route.params.formName);
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}/form/custom-prefills/${encodedFormName}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        this.customPrefillParams = data?.data || {};
      } catch (error) {
        console.warn('[Forms] Failed to load custom prefill params:', error);
        this.customPrefillParams = {};
      } finally {
        this.customPrefillsLoading = false;
      }
    },
    back() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/');
      }
    },
    isIos() {
      return isIosNativeOnly();
    },
  },
};
</script>
